import { useState } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import autoTable from 'jspdf-autotable';
import { toast } from '@/components/ui/use-toast';

interface ExportOptions {
  filename?: string;
  scale?: number;
  quality?: number;
  patientName?: string; // Nom complet du patient pour l'en-tête
}

interface TableColumn {
  key: string;
  header: string;
}

interface TableRow {
  [key: string]: string | number;
}

interface SeancesExportOptions extends ExportOptions {
  dataRows?: TableRow[];
  columns?: TableColumn[];
  selectedExercise?: string;
}

export const useStructuredExportPDF = () => {
  const [isExporting, setIsExporting] = useState(false);

  const captureElement = async (elementId: string, scale = 2) => {
    const element = document.getElementById(elementId);
    if (!element) {
      throw new Error(`Element with id ${elementId} not found`);
    }
    // Ajouter un padding bas temporaire pour éviter que le texte final soit coupé (cas Comparaison Bilans)
    const originalPaddingBottom = element.style.paddingBottom;
    if (!originalPaddingBottom || parseInt(originalPaddingBottom) < 24) {
      element.style.paddingBottom = '24px';
    }

    const canvas = await html2canvas(element, {
      scale,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      removeContainer: false,
      imageTimeout: 15000,
      onclone: (clonedDoc) => {
        // Ensure all fonts are loaded in the cloned document
        const clonedElement = clonedDoc.getElementById(elementId);
        if (clonedElement) {
          clonedElement.style.fontFamily = 'Arial, sans-serif';
          if (!clonedElement.style.paddingBottom || parseInt(clonedElement.style.paddingBottom) < 24) {
            clonedElement.style.paddingBottom = '24px';
          }
        }
      }
    });

    // Restaurer le padding initial
    element.style.paddingBottom = originalPaddingBottom;

    return canvas;
  };

  const addCanvasToPDF = (pdf: jsPDF, canvas: HTMLCanvasElement, yPosition: number, pageWidth: number) => {
    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    const imgWidth = pageWidth - 40; // 20mm margins on each side
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    
    // Check if we need a new page
    if (yPosition + imgHeight > pdf.internal.pageSize.height - 20) {
      pdf.addPage();
      yPosition = 20;
    }
    
    pdf.addImage(imgData, 'JPEG', 20, yPosition, imgWidth, imgHeight);
    return yPosition + imgHeight + 10;
  };

  const exportZF = async (options: ExportOptions = {}) => {
    const { filename = 'resultats-zone-frontiere', scale = 2, patientName } = options;
    
    try {
      setIsExporting(true);
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.width;
      let yPosition = 20;

      // Title
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`Résultats Zone Frontière - ${patientName}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 20;

      // Date
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      const currentDate = new Date().toLocaleDateString('fr-FR');
      pdf.text(`Généré le ${currentDate}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 20;

      // ZF A Section
      const zfAElement = document.getElementById('zf-a-screen');
      if (zfAElement) {
        const caption = zfAElement.getAttribute('data-caption') || 'Zone Frontière A';
        
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text(caption, 20, yPosition);
        yPosition += 10;
        
        const canvas = await captureElement('zf-a-screen', scale);
        yPosition = addCanvasToPDF(pdf, canvas, yPosition, pageWidth);
      }

      // ZF B Section
      const zfBElement = document.getElementById('zf-b-screen');
      if (zfBElement) {
        const caption = zfBElement.getAttribute('data-caption') || 'Zone Frontière B';
        
        if (yPosition > pdf.internal.pageSize.height - 100) {
          pdf.addPage();
          yPosition = 20;
        }
        
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text(caption, 20, yPosition);
        yPosition += 10;
        
        const canvas = await captureElement('zf-b-screen', scale);
        yPosition = addCanvasToPDF(pdf, canvas, yPosition, pageWidth);
      }

      // Bilan Section (from ZF tab)
      const bilanElement = document.getElementById('bilan-screen');
      if (bilanElement) {
        const caption = bilanElement.getAttribute('data-caption') || 'Bilan';
        
        if (yPosition > pdf.internal.pageSize.height - 100) {
          pdf.addPage();
          yPosition = 20;
        }
        
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text(caption, 20, yPosition);
        yPosition += 10;
        
        const canvas = await captureElement('bilan-screen', scale);
        yPosition = addCanvasToPDF(pdf, canvas, yPosition, pageWidth);
      }

      // Save the PDF
      pdf.save(`${filename}.pdf`);
      
      toast({
        title: 'Export réussi',
        description: `Le fichier ${filename}.pdf a été généré avec succès.`,
      });
      
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      toast({
        title: 'Erreur d\'export',
        description: 'Une erreur est survenue lors de l\'export PDF.',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const exportBilan = async (options: ExportOptions = {}) => {
    const { filename = 'resultats-bilan', scale = 2, patientName } = options;
    
    try {
      setIsExporting(true);
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.width;
      let yPosition = 20;

      // Title
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`Résultats Bilan - ${patientName}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 20;

      // Date
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      const currentDate = new Date().toLocaleDateString('fr-FR');
      pdf.text(`Généré le ${currentDate}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 20;

      // Bilan A Section
      const bilanAElement = document.getElementById('bilan-a-screen');
      if (bilanAElement) {
        const caption = bilanAElement.getAttribute('data-caption') || 'Bilan A';
        
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text(caption, 20, yPosition);
        yPosition += 10;
        
        const canvas = await captureElement('bilan-a-screen', scale);
        yPosition = addCanvasToPDF(pdf, canvas, yPosition, pageWidth);
      }

      // Bilan B Section
      const bilanBElement = document.getElementById('bilan-b-screen');
      if (bilanBElement) {
        const caption = bilanBElement.getAttribute('data-caption') || 'Bilan B';
        
        if (yPosition > pdf.internal.pageSize.height - 100) {
          pdf.addPage();
          yPosition = 20;
        }
        
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text(caption, 20, yPosition);
        yPosition += 10;
        
        const canvas = await captureElement('bilan-b-screen', scale);
        yPosition = addCanvasToPDF(pdf, canvas, yPosition, pageWidth);
      }

      // Comparison Section
      const comparisonElement = document.getElementById('bilan-comparison-screen');
      if (comparisonElement) {
        const caption = comparisonElement.getAttribute('data-caption') || 'Comparaison des Bilans';
        
        if (yPosition > pdf.internal.pageSize.height - 100) {
          pdf.addPage();
          yPosition = 20;
        }
        
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text(caption, 20, yPosition);
        yPosition += 10;
        
        const canvas = await captureElement('bilan-comparison-screen', scale);
        yPosition = addCanvasToPDF(pdf, canvas, yPosition, pageWidth);
      }

      // Save the PDF
      pdf.save(`${filename}.pdf`);
      
      toast({
        title: 'Export réussi',
        description: `Le fichier ${filename}.pdf a été généré avec succès.`,
      });
      
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      toast({
        title: 'Erreur d\'export',
        description: 'Une erreur est survenue lors de l\'export PDF.',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const exportSeancesDashboard = async (options: SeancesExportOptions = {}) => {
    const { filename = 'resultats-seances', scale = 2, dataRows, columns, selectedExercise, patientName } = options;
    
    try {
      setIsExporting(true);
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.width;
      let yPosition = 20;

      // Title
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text(`Résultats des Séances de Rééducation - ${patientName}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 20;

      // Date and exercise info
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      const currentDate = new Date().toLocaleDateString('fr-FR');
      pdf.text(`Généré le ${currentDate}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;
      
      if (selectedExercise) {
        const exerciseNames: { [key: string]: string } = {
          'resultsZPL': 'Champ Profond Localisation',
          'resultsZPI': 'Champ Profond Identification', 
          'resultsZPCCF': 'Champ Profond Couleurs',
          'resultsZPFCF': 'Champ Profond Formes',
          'resultsPVS': 'Poursuite Visuelle Smooth',
          'resultsOT': 'Lecture Texte',
          'resultsOB': 'Lecture Barres'
        };
        pdf.text(`Exercice: ${exerciseNames[selectedExercise] || selectedExercise}`, pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 15;
      }

      // Graph Section
      const graphElement = document.getElementById('seances-graph');
      if (graphElement) {
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Évolution des Résultats', 20, yPosition);
        yPosition += 10;
        
        const canvas = await captureElement('seances-graph', scale);
        yPosition = addCanvasToPDF(pdf, canvas, yPosition, pageWidth);
      }

      // Table Section
      if (yPosition > pdf.internal.pageSize.height - 100) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Détail des Séances', 20, yPosition);
      yPosition += 10;

      // Generate table data
      if (dataRows && columns) {
        // Use provided data
        autoTable(pdf, {
          head: [columns.map(col => col.header)],
          body: dataRows.map(row => columns.map(col => String(row[col.key] || ''))),
          startY: yPosition,
          styles: {
            fontSize: 10,
            cellPadding: 3,
          },
          headStyles: {
            fillColor: [66, 139, 202],
            textColor: 255,
            fontStyle: 'bold',
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245],
          },
          margin: { left: 20, right: 20 },
        });
      } else {
        // Parse from DOM as fallback
        const tableElement = document.getElementById('seances-table');
        if (tableElement) {
          const table = tableElement.querySelector('table');
          if (table) {
            autoTable(pdf, {
              html: table,
              startY: yPosition,
              styles: {
                fontSize: 10,
                cellPadding: 3,
              },
              headStyles: {
                fillColor: [66, 139, 202],
                textColor: 255,
                fontStyle: 'bold',
              },
              alternateRowStyles: {
                fillColor: [245, 245, 245],
              },
              margin: { left: 20, right: 20 },
            });
          }
        }
      }

      // Save the PDF
      pdf.save(`${filename}.pdf`);
      
      toast({
        title: 'Export réussi',
        description: `Le fichier ${filename}.pdf a été généré avec succès.`,
      });
      
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      toast({
        title: 'Erreur d\'export',
        description: 'Une erreur est survenue lors de l\'export PDF.',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  return {
    exportBilan,
    exportZF,
    exportSeancesDashboard,
    isExporting,
  };
};
